/**
 * Pure decision helpers backing the FastTrackApi axios interceptors and the
 * anonymous-login bootstrap. Kept side-effect free so the auth/session rules can
 * be unit tested without spinning up axios or React.
 */

/**
 * Applies the required headers to an outgoing request. Throws when the
 * publishable key is missing, mirroring the request interceptor's hard failure.
 */
export const applyRequestHeaders = (
	headers: Record<string, unknown>,
	opts: { publishableKey: string | undefined; token?: string; requestId: string }
) => {
	if (!opts.publishableKey) {
		throw new Error('PUBLISHABLE_KEY is not set')
	}
	headers['request-id'] = opts.requestId
	headers['x-publishable-key'] = opts.publishableKey
	if (opts.token) {
		headers['Authorization'] = `Bearer ${opts.token}`
	}
	return headers
}

/**
 * Whether a failed response should force a logout. Login requests never trigger
 * a logout, and only un-retried 401/403 axios errors do.
 */
export const shouldLogoutOnAuthError = (opts: {
	url?: string
	status?: number
	isAxiosError: boolean
	alreadyRetried?: boolean
}): boolean => {
	if (opts.url?.includes('/login')) {
		return false
	}
	return opts.isAxiosError && (opts.status === 401 || opts.status === 403) && !opts.alreadyRetried
}

/**
 * Whether the API should attempt an anonymous customer login on boot. We only do
 * so when there is no existing token and the tenant does not require an account.
 */
export const shouldBootstrapAnonymousLogin = (opts: { hasToken: boolean; loginRequired: boolean }): boolean =>
	!opts.hasToken && !opts.loginRequired

/**
 * Whether an outgoing request must wait for the anonymous-login bootstrap to
 * finish. Only token-less POSTs (other than the login request itself) wait.
 */
export const shouldWaitForInitialization = (opts: {
	method: string
	hasToken: boolean
	isCustomerLogin: boolean
}): boolean => opts.method === 'POST' && !opts.hasToken && !opts.isCustomerLogin
